import {
  Chip,
  CircularProgress,
  IconButton,
  OutlinedInput,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { Check, Pencil, Shield, Star, Trash2, X } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Tooltip } from 'src/components';

const rowIconButtonSx: SxProps<Theme> = { '& svg': { width: '0.875rem', height: '0.875rem' } };

const favoriteIconButtonSx: SxProps<Theme> = { ...rowIconButtonSx, color: 'warning.main' };

interface RowIconButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  testId?: string;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

const RowIconButton = ({
  label,
  onClick,
  disabled,
  loading,
  testId,
  sx = rowIconButtonSx,
  children,
}: RowIconButtonProps) => (
  <Tooltip title={label}>
    <span>
      <IconButton
        size="small"
        aria-label={label}
        onClick={onClick}
        disabled={disabled || loading}
        data-testid={testId}
        sx={sx}
      >
        {loading ? <CircularProgress size="0.875rem" /> : children}
      </IconButton>
    </span>
  </Tooltip>
);

interface MacroRowActionsProps {
  isFavorite: boolean;
  canToggleFavorite: boolean;
  onToggleFavorite: () => void;
  canEdit: boolean;
  isDeleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

export const MacroRowActions = ({
  isFavorite,
  canToggleFavorite,
  onToggleFavorite,
  canEdit,
  isDeleting,
  onEdit,
  onDelete,
}: MacroRowActionsProps) => (
  <>
    <RowIconButton
      label={isFavorite ? 'Unfavorite macro' : 'Favorite macro'}
      onClick={onToggleFavorite}
      disabled={!canToggleFavorite}
      sx={isFavorite ? favoriteIconButtonSx : rowIconButtonSx}
    >
      <Star fill={isFavorite ? 'currentColor' : 'none'} />
    </RowIconButton>
    {canEdit ? (
      <>
        <RowIconButton label="Edit macro" onClick={onEdit}>
          <Pencil />
        </RowIconButton>
        <RowIconButton
          label="Delete macro"
          onClick={onDelete}
          loading={isDeleting}
          testId="macros-modal-macro-delete"
        >
          <Trash2 />
        </RowIconButton>
      </>
    ) : null}
  </>
);

interface FolderRowMetaProps {
  count: number;
  isPlatform: boolean;
}

export const FolderRowMeta = ({ count, isPlatform }: FolderRowMetaProps) => (
  <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
    <Typography variant="caption" color="text.secondary">
      {count}
    </Typography>
    {isPlatform ? (
      <Chip size="small" variant="outlined" icon={<Shield />} label="Platform" />
    ) : null}
  </Stack>
);

interface FolderRowActionsProps {
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onStartRename: () => void;
  onSaveName: () => void;
  onCancelRename: () => void;
  onDelete: () => void;
}

export const FolderRowActions = ({
  isEditing,
  isSaving,
  isDeleting,
  onStartRename,
  onSaveName,
  onCancelRename,
  onDelete,
}: FolderRowActionsProps) =>
  isEditing ? (
    <>
      <RowIconButton
        label="Save name"
        onClick={onSaveName}
        loading={isSaving}
        testId="macros-modal-folder-rename-save"
      >
        <Check />
      </RowIconButton>
      <RowIconButton label="Cancel" onClick={onCancelRename}>
        <X />
      </RowIconButton>
    </>
  ) : (
    <>
      <RowIconButton label="Rename folder" onClick={onStartRename}>
        <Pencil />
      </RowIconButton>
      <RowIconButton
        label="Delete folder"
        onClick={onDelete}
        loading={isDeleting}
        testId="macros-modal-folder-delete"
      >
        <Trash2 />
      </RowIconButton>
    </>
  );

interface NewFolderRowActionsProps {
  canCreate: boolean;
  isCreating: boolean;
  onCreate: () => void;
  onCancel: () => void;
}

export const NewFolderRowActions = ({
  canCreate,
  isCreating,
  onCreate,
  onCancel,
}: NewFolderRowActionsProps) => (
  <>
    <RowIconButton
      label="Create folder"
      onClick={onCreate}
      disabled={!canCreate}
      loading={isCreating}
      testId="macros-modal-folder-create"
    >
      <Check />
    </RowIconButton>
    <RowIconButton label="Cancel" onClick={onCancel}>
      <X />
    </RowIconButton>
  </>
);

interface FolderNameEditorProps {
  value: string;
  label: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  testId?: string;
}

export const FolderNameEditor = ({
  value,
  label,
  onChange,
  onSubmit,
  onCancel,
  testId,
}: FolderNameEditorProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <OutlinedInput
      autoFocus
      fullWidth
      size="small"
      value={value}
      placeholder="Folder name"
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={handleKeyDown}
      inputProps={{ 'aria-label': label, 'data-testid': testId }}
    />
  );
};
